import React, { useEffect, useState, useRef } from 'react';
import api from '../lib/api';
import { Sparkle, PaperPlaneRight, Clock } from '@phosphor-icons/react';

export default function AIAdvisorPage() {
  const [question, setQuestion] = useState('');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const responseRef = useRef(null);

  useEffect(() => {
    api.get('/ai/history').then(r => setHistory(r.data)).catch(() => {});
  }, []);

  const handleAsk = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    setLoading(true);
    setResponse('');
    try {
      const { data } = await api.post('/ai/advice', { question: question.trim() });
      setResponse(data.response);
      setHistory(prev => [{ question: data.question, response: data.response, created_at: new Date().toISOString() }, ...prev]);
      setQuestion('');
    } catch (err) {
      setResponse('Sorry, I encountered an error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const suggestions = [
    "How can I reduce my monthly expenses?",
    "What's the best strategy for my current financial situation?",
    "Should I invest more or pay off debt first?",
    "How can I build an emergency fund?",
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ai-advisor-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#1C1B1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>AI Financial Advisor</h1>
        <p className="text-[#5C5A56] text-sm mt-1">Get personalized financial insights powered by AI</p>
      </div>

      {/* AI Chat Card */}
      <div className="bg-gradient-to-br from-[#FDF7F0] to-[#F5EBE1] border border-[#E8DCCB] rounded-2xl shadow-sm p-6 relative overflow-hidden" data-testid="ai-chat-card">
        <div className="flex items-center gap-2 mb-4">
          <Sparkle size={22} weight="fill" className="text-[#D98324]" />
          <h3 className="text-lg font-semibold text-[#1C1B1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>Ask Spell FinX AI</h3>
        </div>

        <form onSubmit={handleAsk} className="flex gap-3 mb-4">
          <input
            data-testid="ai-question-input"
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask about your finances..."
            className="flex-1 bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#D98324]/20 focus:border-[#D98324] transition-all"
            disabled={loading}
          />
          <button
            data-testid="ai-submit-btn"
            type="submit"
            disabled={loading || !question.trim()}
            className="bg-[#D98324] text-white font-semibold rounded-xl px-5 py-3 hover:bg-[#F09B35] transition-colors active:scale-95 disabled:opacity-60 flex items-center gap-2"
          >
            <PaperPlaneRight size={18} weight="bold" />
          </button>
        </form>

        {/* Suggestions */}
        {!response && !loading && (
          <div className="flex flex-wrap gap-2 mb-4">
            {suggestions.map((s, i) => (
              <button
                key={i}
                onClick={() => setQuestion(s)}
                className="text-xs bg-white/80 border border-[#E8DCCB] text-[#5C5A56] rounded-lg px-3 py-1.5 hover:bg-white hover:text-[#1C1B1A] transition-colors"
                data-testid={`suggestion-${i}`}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center gap-3 py-4" data-testid="ai-loading">
            <div className="w-5 h-5 border-2 border-[#D98324] border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-[#5C5A56]">Analyzing your finances...</span>
          </div>
        )}

        {/* Response */}
        {response && !loading && (
          <div ref={responseRef} className="bg-white/80 border border-[#E8DCCB] rounded-xl p-5 mt-2" data-testid="ai-response">
            <div className="prose prose-sm max-w-none text-[#1C1B1A] leading-relaxed whitespace-pre-wrap">
              {response}
            </div>
          </div>
        )}

        <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#D98324]/10 rounded-full blur-3xl" />
      </div>

      {/* History */}
      {history.length > 0 && (
        <div>
          <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-[#5C5A56] mb-3 flex items-center gap-2">
            <Clock size={16} /> Previous Conversations
          </h3>
          <div className="space-y-3" data-testid="ai-history">
            {history.slice(0, 10).map((h, i) => (
              <div key={i} className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-4 hover:shadow-md transition-all" data-testid={`history-${i}`}>
                <p className="text-sm font-semibold text-[#1E3F2D] mb-2">Q: {h.question}</p>
                <p className="text-sm text-[#5C5A56] leading-relaxed line-clamp-3 whitespace-pre-wrap">{h.response}</p>
                <p className="text-xs text-[#5C5A56]/60 mt-2">{new Date(h.created_at).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
